import React, { useEffect, useRef, useCallback } from 'react';
import gsap from 'gsap';

interface VenomTransitionProps {
    isActive: boolean;
    onCovered: () => void;
    onComplete: () => void;
}

const VenomTransition: React.FC<VenomTransitionProps> = ({
    isActive,
    onCovered,
    onComplete
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const animationFrameRef = useRef<number>(0);

    // --- Sound Logic (Kept same as it works well) ---
    const playLiquidSound = useCallback(() => {
        try {
            if (!audioContextRef.current) {
                const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
                audioContextRef.current = new (AudioContextClass as any)();
            }

            const ctx = audioContextRef.current!;
            const duration = 2.0; // Slightly longer for smoothness

            const bufferSize = ctx.sampleRate * duration;
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);

            let lastOut = 0;
            for (let i = 0; i < bufferSize; i++) {
                const white = Math.random() * 2 - 1;
                data[i] = (lastOut + (0.02 * white)) / 1.02;
                lastOut = data[i];
                data[i] *= 3.5;
                data[i] *= Math.sin(Math.PI * i / bufferSize) * 0.3; // Envelope
            }

            const noise = ctx.createBufferSource();
            noise.buffer = buffer;

            const filter = ctx.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.value = 400;
            filter.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + duration);

            const gainNode = ctx.createGain();
            gainNode.gain.setValueAtTime(0.6, ctx.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

            noise.connect(filter);
            filter.connect(gainNode);
            gainNode.connect(ctx.destination);

            noise.start();
            noise.stop(ctx.currentTime + duration);
        } catch (e) {
            // Audio fallback
        }
    }, []);

    useEffect(() => {
        if (!isActive || !canvasRef.current || !containerRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d')!;
        const container = containerRef.current;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        playLiquidSound();

        // --- Realistic Fluid Math ---

        // Simple fast noise function (Permutation table based)
        const perm = new Uint8Array(512);
        for (let i = 0; i < 256; i++) perm[i] = perm[i + 256] = Math.floor(Math.random() * 256);

        const lerp = (a: number, b: number, t: number) => a + (b - a) * (1 - Math.cos(t * Math.PI)) / 2;

        const noise2D = (x: number, y: number) => {
            const X = Math.floor(x) & 255;
            const Y = Math.floor(y) & 255;
            const xf = x - Math.floor(x);
            const yf = y - Math.floor(y);

            const n00 = perm[X + perm[Y]];
            const n01 = perm[X + perm[Y + 1]];
            const n10 = perm[X + 1 + perm[Y]];
            const n11 = perm[X + 1 + perm[Y + 1]];

            const v00 = (n00 / 255) * 2 - 1;
            const v01 = (n01 / 255) * 2 - 1;
            const v10 = (n10 / 255) * 2 - 1;
            const v11 = (n11 / 255) * 2 - 1;

            const val = lerp(
                lerp(v00, v10, xf),
                lerp(v01, v11, xf),
                yf
            );
            return val; // Range roughly -1 to 1
        };

        // Class representing a spreading fluid mass
        class FluidBlob {
            x: number;
            y: number;
            radius: number;
            growthRate: number;
            timeOff: number;

            constructor(x: number, y: number) {
                this.x = x;
                this.y = y;
                this.radius = 0;
                this.growthRate = Math.max(window.innerWidth, window.innerHeight) * 0.012; // Slightly slower base for smoothness
                this.timeOff = Math.random() * 100;
            }

            update() {
                // Accelerate expansion
                this.radius += this.growthRate;
                this.growthRate *= 1.018; // Slightly reduced acceleration for control
            }

            draw(ctx: CanvasRenderingContext2D, time: number) {
                ctx.beginPath();

                const points = 120; // Number of vertices for the blob
                const noiseScale = 0.005; // Smoothness of the blob shape
                const amplitude = this.radius * 0.3; // How "goopy"/irregular the edge is

                for (let i = 0; i <= points; i++) {
                    const angle = (i / points) * Math.PI * 2;

                    // Noise driven radius offset
                    // We march noise through time and space (angle)
                    // The 'distortion' helps it look like it's churning
                    const n = noise2D(
                        Math.cos(angle) * 3 + time * 0.5 + this.timeOff,
                        Math.sin(angle) * 3 + time * 0.5 + this.timeOff
                    );

                    // Radius calculation
                    const r = this.radius + (n * amplitude);

                    // Convert polar to cartesian
                    const px = this.x + Math.cos(angle) * r;
                    const py = this.y + Math.sin(angle) * r;

                    if (i === 0) ctx.moveTo(px, py);
                    else ctx.lineTo(px, py);
                }

                ctx.closePath();

                // Fluid Look: Dark core, slightly lighter edge (specular simulation via gradient)
                // Since this draws FROM corner, we need a gradient relative to the blob size
                const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius * 1.5);
                grad.addColorStop(0, '#000000');
                grad.addColorStop(0.5, '#0a0a0a');
                grad.addColorStop(1, '#050505');

                ctx.fillStyle = grad;
                ctx.fill();

                // Draw Gloss/Reflection (The "Wet" part)
                // We draw a smaller, offset stroke or region
                ctx.save();
                ctx.clip(); // Clip to the blob shape

                // Add some "highlights" based on noise thresholds
                // This simulates light catching the ripples
                ctx.globalCompositeOperation = 'source-atop';
                ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
                ctx.beginPath();
                ctx.arc(this.x - 50, this.y - 50, this.radius * 0.8, 0, Math.PI * 2);
                ctx.fill();

                ctx.restore();
            }
        }

        const blobs: FluidBlob[] = [
            new FluidBlob(0, 0),
            new FluidBlob(canvas.width, 0),
            new FluidBlob(canvas.width, canvas.height),
            new FluidBlob(0, canvas.height)
        ];

        let time = 0;
        let isFinishing = false;

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            time += 0.02;

            let maxRadius = 0;
            const targetRadius = Math.hypot(canvas.width, canvas.height);

            // Draw all blobs
            blobs.forEach(blob => {
                blob.update();
                blob.draw(ctx, time);
                if (blob.radius > maxRadius) maxRadius = blob.radius;
            });

            // Check if covered
            // We use a safe margin to ensure corners met in middle
            if (maxRadius > targetRadius * 0.8 && !isFinishing) {
                isFinishing = true;

                // Screen is effectively covered, switch content now
                onCovered();

                // Fill remaining gaps with a solid fade to ensure full coverage
                const fillTl = gsap.timeline({
                    onComplete: () => {
                        // Fade out entire container to reveal site
                        gsap.to(container, {
                            opacity: 0,
                            duration: 0.8, // Slower fade
                            ease: 'power2.inOut',
                            onComplete
                        });
                    }
                });

                // Flash/Solidify
                fillTl.to(canvas, {
                    duration: 0.2, // Slightly longer solid hold
                    onUpdate: () => {
                        ctx.globalCompositeOperation = 'source-over';
                        ctx.fillStyle = '#0F0C0A'; // Final BG color
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                    }
                });
            }

            if (!isFinishing) {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };

        gsap.set(container, { opacity: 1 });
        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };
    }, [isActive, onComplete, playLiquidSound]);

    if (!isActive) return null;

    return (
        <div
            ref={containerRef}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                background: 'transparent',
                pointerEvents: 'none' // Click-through enabled
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    display: 'block',
                    width: '100%',
                    height: '100%'
                }}
            />
        </div>
    );
};

export default VenomTransition;
